import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ThriveTitle from "../common/ThriveTitle";
import { StyledButtonMain } from "../common/Buttons";
import { useState } from "react";
import { axiosInstance } from "../../utils/networking";

const UserEditor = (props: any) => {
  const [user, setUser] = useState(props.user);
  // New users are onboarded via an email invite by default. Admins can opt
  // into setting a password manually (legacy override).
  const [manualPassword, setManualPassword] = useState(false);
  // Service accounts / admins can be hidden from consultant statistics.
  const [hideFromStats, setHideFromStats] = useState<boolean>(
    props.user?.hideFromStats === true
  );

  // /auth/register and /auth/update only accept a fixed field list, so the
  // stats-visibility flag is persisted through its own admin-only route right
  // after the main save succeeds.
  const saveHideFromStats = (userId: string) =>
    axiosInstance
      .post(`/user/hideFromStats`, {
        user_id: userId,
        hideFromStats: hideFromStats,
      })
      .catch((err) => {
        console.log("Failed to save hide-from-statistics flag", err);
      });

  const handleSaveUser = (event: any) => {
    event.preventDefault();
    if (!user._id) {
      const payload: any = {
        ...user,
        hideFromStats: hideFromStats,
        title: "Small Business Consultant, Oakland Thrive",
      };
      if (!manualPassword) {
        delete payload.password;
      }
      axiosInstance.post(`/auth/register`, payload).then(async (res) => {
        const newId = res?.data?.userId;
        if (newId && hideFromStats) {
          await saveHideFromStats(String(newId));
        }
        if (!manualPassword) {
          alert(`Invite email sent to ${user.email}`);
        }
        props.setOpen(false);
        window.location.reload();
      });
    } else {
      axiosInstance
        .post(`/auth/update`, {
          ...user,
        })
        .then(async () => {
          await saveHideFromStats(String(user._id));
          props.setOpen(false);
          window.location.reload();
        });
    }
  };

  const sendPasswordReset = () => {
    axiosInstance
      .post(`/auth/request-setup`, { userId: user._id })
      .finally(() => alert(`Password reset link sent to ${user.email}`));
  };

  return (
    <form
      onSubmit={handleSaveUser}
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <ThriveTitle title="Edit User" setOpen={props.setOpen}></ThriveTitle>
      <Stack
        sx={{ padding: "20px", flex: 1, minHeight: 0, overflowY: "auto" }}
        spacing={2}
      >
        <TextField
          label={`Username`}
          variant="outlined"
          fullWidth
          className="mt-2"
          value={user.username ? user.username : ""}
          onChange={(e) => {
            setUser({ ...user, username: e.target.value });
          }}
          required
        ></TextField>
        {!user._id ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={manualPassword}
                onChange={(e) => setManualPassword(e.target.checked)}
              />
            }
            label="Set password manually (skip email invite)"
          />
        ) : null}
        {!user._id && !manualPassword ? (
          <Typography variant="body2" sx={{ color: "#666" }}>
            An invite email will be sent so the user can set their own password.
          </Typography>
        ) : (
          <TextField
            type="password"
            label={`Password`}
            variant="outlined"
            fullWidth
            className="mt-2"
            helperText={
              user._id ? "Leave blank to keep the current password" : ""
            }
            value={user.password ? user.password : ""}
            onChange={(e) => {
              setUser({ ...user, password: e.target.value });
            }}
            required={!user._id && manualPassword}
          ></TextField>
        )}
        {user._id && user.role !== "deleted" && user.status !== "inactive" ? (
          <Button
            type="button"
            variant="text"
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
            onClick={sendPasswordReset}
          >
            Send password reset link
          </Button>
        ) : null}

        <Stack direction="row" spacing={2}>
          {" "}
          <TextField
            label={`First Name`}
            variant="outlined"
            fullWidth
            className="mt-2"
            value={user.firstName ? user.firstName : ""}
            onChange={(e) => {
              setUser({ ...user, firstName: e.target.value });
            }}
            required
          ></TextField>
          <TextField
            label={`Last Name`}
            variant="outlined"
            fullWidth
            className="mt-2"
            value={user.lastName ? user.lastName : ""}
            onChange={(e) => {
              setUser({ ...user, lastName: e.target.value });
            }}
            required
          ></TextField>
          <FormControl fullWidth>
            <InputLabel required id="role-label">{`Role`}</InputLabel>
            <Select
              labelId="role-label"
              id="demo-simple-select"
              label={`Role`}
              value={user.role ? user.role : ""}
              onChange={(e) => {
                setUser({ ...user, role: e.target.value });
              }}
              required
            >
              <MenuItem value={"bfc"}>Small Business Consultant</MenuItem>
              <MenuItem value={"admin"}>{"Admin"}</MenuItem>
              <MenuItem value={"deleted"}>{"Disabled"}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <TextField
          label={`Title`}
          variant="outlined"
          fullWidth
          className="mt-2"
          value={user.title ? user.title : ""}
          onChange={(e) => {
            setUser({ ...user, title: e.target.value });
          }}
          required
        ></TextField>
        <TextField
          label={`Email`}
          variant="outlined"
          fullWidth
          className="mt-2"
          value={user.email ? user.email : ""}
          onChange={(e) => {
            setUser({ ...user, email: e.target.value });
          }}
          required
        ></TextField>
        <TextField
          label={`Phone`}
          variant="outlined"
          fullWidth
          className="mt-2"
          value={user.phone ? user.phone : ""}
          onChange={(e) => {
            setUser({ ...user, phone: e.target.value });
          }}
          required
        ></TextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={hideFromStats}
              onChange={(e) => setHideFromStats(e.target.checked)}
            />
          }
          label="Hide from statistics"
          sx={{ alignSelf: "flex-start" }}
        />
        <Typography variant="body2" sx={{ color: "#666", mt: "0 !important" }}>
          Excluded from consultant statistics and reports
        </Typography>
        <TextField
          label={`Meeting Link`}
          variant="outlined"
          fullWidth
          className="mt-2"
          value={user.meeting_url ? user.meeting_url : ""}
          onChange={(e) => {
            setUser({ ...user, meeting_url: e.target.value });
          }}
          required
        ></TextField>
        <Box
          sx={{
            flexGrow: 1,
            display: { xs: "none", md: "flex" },
            width: "100%",
            textAlign: "center",
          }}
        >
          <StyledButtonMain type="submit" fullWidth variant="contained">
            Save
          </StyledButtonMain>
        </Box>
        {user._id &&
        user.role !== "deleted" &&
        user.status !== "inactive" &&
        props.onOffboard ? (
          <Button
            type="button"
            fullWidth
            variant="outlined"
            color="error"
            onClick={() => props.onOffboard(user)}
          >
            Offboard user (reassign cases & deactivate)
          </Button>
        ) : null}
        {user._id && (user.role === "deleted" || user.status === "inactive") ? (
          <Typography sx={{ textAlign: "center", color: "#9E1F63" }}>
            {user.deactivatedAt
              ? `Deactivated on ${String(user.deactivatedAt).slice(0, 10)}`
              : "This account is deactivated."}
          </Typography>
        ) : null}
      </Stack>
    </form>
  );
};

export default UserEditor;
